import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Modal, Tag, Dropdown, Menu, notification } from 'antd';
import { connect, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl, FormattedPlural } from 'react-intl';

import {
  inviteToWorkspace,
  generateWorkspaceInviteLink,
  getWorkspaceInviteLinks,
  revokeWorkspaceInviteLink,
} from '../../../../actions';
import { toggleInviteToWorkspaceModal } from '../../../../actions/modalAction';
import WorkspaceMemberRow from '../../atoms/invite-modal/WorkspaceMemberRow';
import { getUserLicense } from '../../../../js/functions';

const isEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const RevokePopover = ({ onConfirm }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        popRef.current && !popRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.top - 8, left: r.right });
    }
    setOpen(v => !v);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="text-xs font-medium transition-colors px-2.5 py-1.5 rounded-lg"
        style={{ color: '#ef4444', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.15)' }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.13)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.07)'}
      >
        Revoke
      </button>
      {open && (
        <div
          ref={popRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translate(-100%, -100%)', zIndex: 9999, background: 'white', border: '1px solid rgba(99,102,241,0.12)', boxShadow: '0 8px 24px rgba(99,102,241,0.12)', borderRadius: 12 }}
          className="w-64 p-4"
        >
          <div className="flex items-start gap-2.5 mb-3">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(245,158,11,0.1)' }}>
              <svg className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold leading-snug" style={{ color: 'rgba(15,15,25,0.85)' }}>Revoke this link?</p>
              <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'rgba(15,15,25,0.45)' }}>Anyone with this link will no longer be able to join.</p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setOpen(false)}
              className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
              style={{ color: 'rgba(15,15,25,0.5)', background: 'rgba(15,15,25,0.04)', border: '1px solid rgba(15,15,25,0.1)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(15,15,25,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(15,15,25,0.04)'}
            >
              Cancel
            </button>
            <button
              onClick={() => { setOpen(false); onConfirm(); }}
              className="text-xs font-semibold text-white px-3 py-1.5 rounded-lg transition-colors"
              style={{ background: '#ef4444' }}
              onMouseEnter={e => e.currentTarget.style.background = '#dc2626'}
              onMouseLeave={e => e.currentTarget.style.background = '#ef4444'}
            >
              Revoke
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const EXPIRY_OPTIONS = [
  { label: '1 day', value: '1d', days: 1 },
  { label: '7 days', value: '7d', days: 7 },
  { label: '30 days', value: '30d', days: 30 },
];

const InviteToWorkspaceModal = ({ isModalOpen, currentWorkspace, user, workspaceRole }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const [activeTab, setActiveTab] = useState('email');
  const [search, setSearch] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [role, setRole] = useState('member');
  const [linkRole, setLinkRole] = useState('member');
  const [linkExpiry, setLinkExpiry] = useState('7d');
  const [isGenerating, setIsGenerating] = useState(false);
  const [inviteLinks, setInviteLinks] = useState([]);
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);

  const workspaceId = currentWorkspace?._id;
  const isMember = workspaceRole === 'member' || workspaceRole === 'guest';

  const license = getUserLicense(user, sessionStorage.getItem("organization") || localStorage.getItem("organization")) || 'Free Plan';
  const roles = license === 'Team Plan' ? ['admin', 'member', 'guest'] : ['admin', 'member'];

  const closeModal = () => {
    dispatch(toggleInviteToWorkspaceModal(false));
    setSearch('');
    setSelectedMembers([]);
    setInviteLinks([]);
    setActiveTab('email');
    setIsGenerating(false);
    setIsLoadingLinks(false);
  };

  const fetchInviteLinks = useCallback(() => {
    setIsLoadingLinks(true);
    dispatch(getWorkspaceInviteLinks(workspaceId, (data) => {
      setInviteLinks(data ?? []);
      setIsLoadingLinks(false);
    }));
  }, [dispatch, workspaceId]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'link') fetchInviteLinks();
  };

  const handleGenerateLink = () => {
    const days = EXPIRY_OPTIONS.find((o) => o.value === linkExpiry)?.days ?? 7;
    setIsGenerating(true);
    dispatch(generateWorkspaceInviteLink(
      workspaceId,
      { role: linkRole, days },
      (data) => {
        setIsGenerating(false);
        if (data) fetchInviteLinks();
      },
    ));
  };

  const handleSearchEnter = () => {
    const trimmed = search.trim();
    if (!trimmed) return;
    const alreadyExists = selectedMembers.some(
      (item) => item.email.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (alreadyExists) { setSearch(""); return; }
    const isValid = isEmail(trimmed);
    const emailss = trimmed?.replace(',', ' ')?.split(' ');
    if (emailss?.length > 0) {
      let emails = [];
      emailss?.map(e => {
        if (isEmail(e)) emails.push({ email: e, role: role, userId: user?._id });
      });
      setSelectedMembers((prev) => [...prev, ...emails]);
    }
    setSearch('');
    if (!isValid) setIsFocused(false);
  };

  const removeEmail = (emailToRemove) => {
    setSelectedMembers((prev) =>
      prev.filter((item) => item.email.trim().toLowerCase() !== emailToRemove.trim().toLowerCase())
    );
  };

  const invalidEmailsLength = selectedMembers.filter((item) => item.isInvalid).length;
  const canInvite = selectedMembers.length > 0 && !selectedMembers.some(t => t.isInvalid);

  return (
    <Modal
      title={null}
      open={isModalOpen}
      onCancel={closeModal}
      okButtonProps={{ style: { display: 'none' } }}
      cancelButtonProps={{ style: { display: 'none' } }}
      footer={null}
      width={600}
      closable={false}
      styles={{
        content: { padding: 0, borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(99,102,241,0.12)', boxShadow: '0 16px 48px rgba(99,102,241,0.14)' },
        mask: { backdropFilter: 'blur(4px)', background: 'rgba(15,15,25,0.25)' },
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99,102,241,0.1)' }}>
        <h2 className="text-base font-semibold" style={{ color: 'rgba(15,15,25,0.88)' }}>
          {isMember ? currentWorkspace?.title : `${intl.formatMessage({ id: 'INVITE_PEOPLE_TO', defaultMessage: 'Invite members to' })} ${currentWorkspace?.title ?? 'workspace'}`}
        </h2>
        <button
          onClick={closeModal}
          className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors"
          style={{ color: 'rgba(15,15,25,0.35)' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; e.currentTarget.style.color = 'rgba(99,102,241,0.8)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'rgba(15,15,25,0.35)'; }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="px-6 py-4">
        {/* Tabs */}
        {!isMember && (
          <div className="flex items-center gap-0.5 rounded-lg p-0.5 mb-4 w-fit" style={{ background: 'rgba(99,102,241,0.08)' }}>
            {['email', 'link'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className="h-7 px-3.5 rounded-md text-xs font-medium transition-all duration-150"
                style={activeTab === tab
                  ? { background: 'white', color: 'rgba(99,102,241,0.9)', boxShadow: '0 1px 4px rgba(99,102,241,0.15)' }
                  : { color: 'rgba(15,15,25,0.45)' }}
              >
                {tab === 'email' ? 'Invite by Email' : 'Invite with Link'}
              </button>
            ))}
          </div>
        )}

        {/* Email tab */}
        {activeTab === 'email' && !isMember && (
          <>
            <p className="text-xs font-semibold mb-1.5" style={{ color: 'rgba(15,15,25,0.45)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <FormattedMessage id="EMAIL_ADDRESS" defaultMessage="Email Address" />
            </p>
            <div
              className="flex items-start w-full px-3 py-2 rounded-xl gap-1 mb-1"
              style={{
                border: isFocused ? '1.5px solid rgba(99,102,241,0.5)' : invalidEmailsLength > 0 ? '1.5px solid rgba(239,68,68,0.5)' : '1.5px solid rgba(99,102,241,0.18)',
                background: 'rgba(99,102,241,0.02)',
                boxShadow: isFocused ? '0 0 0 3px rgba(99,102,241,0.08)' : 'none',
              }}
            >
              <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
                {selectedMembers.map((member) => (
                  <Tag
                    key={member?.email}
                    closable
                    onClose={() => removeEmail(member?.email)}
                    className={`flex gap-1 items-center max-w-full mr-0 ${member?.isInvalid ? "bg-red-50 text-red-700 border-red-200" : ""}`}
                    style={!member?.isInvalid ? { background: 'rgba(99,102,241,0.08)', color: '#4f46e5', borderColor: 'rgba(99,102,241,0.2)', borderRadius: 6 } : {}}
                  >
                    <span className="truncate max-w-full inline-block text-xs">{member?.email}</span>
                  </Tag>
                ))}
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearchEnter(); }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => { handleSearchEnter(); setIsFocused(false); }}
                  placeholder={selectedMembers.length === 0 ? intl.formatMessage({ id: 'SEARCH_INVITE_TO_WORKSPACE_MODAL', defaultMessage: 'name@gmail.com, name@gmail.com,...' }) : ""}
                  className="border-none flex-1 min-w-[120px] outline-none focus:outline-none bg-transparent text-sm"
                  style={{ color: 'rgba(15,15,25,0.8)' }}
                />
              </div>
              <Dropdown
                overlay={
                  <Menu onClick={({ key }) => {
                    setSelectedMembers(selectedMembers?.map(m => ({ ...m, role: key })));
                    setRole(key);
                  }}>
                    {roles.map((r) => (
                      <Menu.Item className="!text-xs" key={r}>
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </Menu.Item>
                    ))}
                  </Menu>
                }
                trigger={["click"]}
              >
                <div className="flex items-center gap-1 text-xs cursor-pointer rounded-lg px-2 py-1 flex-shrink-0 transition-colors" style={{ color: 'rgba(15,15,25,0.6)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <p className="font-medium">{role.charAt(0).toUpperCase() + role.slice(1)}</p>
                  <img src="/images/icons/black-arrow-down.svg" alt="icon" className="mt-[1px] w-2 h-2 opacity-40" />
                </div>
              </Dropdown>
            </div>
            {invalidEmailsLength > 0 && (
              <p className="text-xs mt-1 ml-1" style={{ color: '#dc2626' }}>
                <FormattedMessage id="ENTERED_TEXT_MESSAGE" defaultMessage="You have entered" />
                {" "}{invalidEmailsLength}{" "}
                <FormattedPlural value={invalidEmailsLength}
                  one={intl.formatMessage({ id: 'INVALID_EMAIL', defaultMessage: 'Invalid email' })}
                  other={intl.formatMessage({ id: 'INVALID_EMAILS', defaultMessage: 'Invalid emails' })}
                />.
              </p>
            )}
          </>
        )}

        {/* Link tab */}
        {activeTab === 'link' && !isMember && (
          <div className="py-1">
            <div className="flex items-center gap-2 mb-5">
              {[
                { label: 'Role', content: (
                  <Dropdown overlay={<Menu onClick={({ key }) => setLinkRole(key)}>{roles.map((r) => <Menu.Item className="!text-xs" key={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</Menu.Item>)}</Menu>} trigger={["click"]}>
                    <div className="flex items-center gap-1.5 text-sm cursor-pointer px-3 py-2 rounded-xl min-w-[110px] transition-colors" style={{ border: '1.5px solid rgba(99,102,241,0.18)', background: 'rgba(99,102,241,0.02)', color: 'rgba(15,15,25,0.75)' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.18)'}
                    >
                      <span className="font-medium">{linkRole.charAt(0).toUpperCase() + linkRole.slice(1)}</span>
                      <img src="/images/icons/black-arrow-down.svg" alt="" className="w-2.5 h-2.5 ml-auto opacity-40" />
                    </div>
                  </Dropdown>
                )},
                { label: 'Expires in', content: (
                  <Dropdown overlay={<Menu onClick={({ key }) => setLinkExpiry(key)}>{EXPIRY_OPTIONS.map((o) => <Menu.Item className="!text-xs" key={o.value}>{o.label}</Menu.Item>)}</Menu>} trigger={["click"]}>
                    <div className="flex items-center gap-1.5 text-sm cursor-pointer px-3 py-2 rounded-xl min-w-[110px] transition-colors" style={{ border: '1.5px solid rgba(99,102,241,0.18)', background: 'rgba(99,102,241,0.02)', color: 'rgba(15,15,25,0.75)' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.18)'}
                    >
                      <span className="font-medium">{EXPIRY_OPTIONS.find((o) => o.value === linkExpiry)?.label}</span>
                      <img src="/images/icons/black-arrow-down.svg" alt="" className="w-2.5 h-2.5 ml-auto opacity-40" />
                    </div>
                  </Dropdown>
                )},
              ].map(({ label, content }) => (
                <div key={label} className="flex flex-col gap-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'rgba(15,15,25,0.35)' }}>{label}</p>
                  {content}
                </div>
              ))}
              <div className="flex flex-col gap-1">
                <p className="text-[11px] invisible">_</p>
                <button
                  disabled={isGenerating}
                  onClick={handleGenerateLink}
                  className="text-sm px-4 py-2 rounded-xl font-medium transition-all duration-150"
                  style={isGenerating
                    ? { background: 'rgba(99,102,241,0.05)', color: 'rgba(99,102,241,0.4)', border: '1.5px solid rgba(99,102,241,0.1)', cursor: 'not-allowed' }
                    : { background: 'rgba(99,102,241,0.08)', color: 'rgba(99,102,241,0.9)', border: '1.5px solid rgba(99,102,241,0.2)' }}
                  onMouseEnter={e => { if (!isGenerating) { e.currentTarget.style.background = 'rgba(99,102,241,0.15)'; } }}
                  onMouseLeave={e => { if (!isGenerating) { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; } }}
                >
                  {isGenerating ? 'Generating…' : 'Generate Link'}
                </button>
              </div>
            </div>

            {isLoadingLinks ? (
              <div className="flex items-center gap-2 py-4 text-sm" style={{ color: 'rgba(15,15,25,0.35)' }}>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Loading…
              </div>
            ) : inviteLinks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-center rounded-xl" style={{ background: 'rgba(99,102,241,0.04)', border: '1.5px dashed rgba(99,102,241,0.2)' }}>
                <svg className="w-8 h-8 mb-2" style={{ color: 'rgba(99,102,241,0.3)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <p className="text-sm font-medium" style={{ color: 'rgba(99,102,241,0.5)' }}>No invite links yet.</p>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(15,15,25,0.3)' }}>Generate one above to share.</p>
              </div>
            ) : (
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99,102,241,0.1)' }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: 'rgba(99,102,241,0.04)', borderBottom: '1px solid rgba(99,102,241,0.08)' }}>
                      {['Role', 'Expires', 'Used', 'Status', ''].map((h, i) => (
                        <th key={i} className={`${i < 4 ? 'text-left' : ''} px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide`} style={{ color: 'rgba(15,15,25,0.35)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {inviteLinks.map((link, idx) => {
                      const isExpired = new Date(link.expiresAt) < new Date();
                      const isRevoked = link.revoked;
                      const isActive = !isExpired && !isRevoked;
                      const statusLabel = isRevoked ? 'Revoked' : isExpired ? 'Expired' : 'Active';
                      return (
                        <tr key={link.token} style={{ borderBottom: idx < inviteLinks.length - 1 ? '1px solid rgba(99,102,241,0.06)' : 'none', opacity: isActive ? 1 : 0.4 }}>
                          <td className="px-4 py-3 capitalize font-medium" style={{ color: 'rgba(15,15,25,0.8)' }}>{link.role}</td>
                          <td className="px-4 py-3 whitespace-nowrap" style={{ color: 'rgba(15,15,25,0.5)' }}>{new Date(link.expiresAt).toLocaleDateString('en-us', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                          <td className="px-4 py-3" style={{ color: 'rgba(15,15,25,0.5)' }}>{link.usageCount}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: isActive ? '#16a34a' : 'rgba(15,15,25,0.35)' }}>
                              {isActive && <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#22c55e' }} />}
                              {statusLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {!isRevoked && (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/join/${link.token}`); notification.success({ message: 'Invite link copied', placement: 'bottomRight' }); }}
                                  className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors"
                                  style={{ color: 'rgba(15,15,25,0.55)', background: 'rgba(15,15,25,0.04)', border: '1px solid rgba(15,15,25,0.1)' }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(15,15,25,0.04)'}
                                >
                                  <img src="/images/icons/copy.svg" alt="" className="w-3 h-3" />
                                  Copy
                                </button>
                                {isActive && <RevokePopover onConfirm={() => dispatch(revokeWorkspaceInviteLink(workspaceId, link.token, fetchInviteLinks))} />}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Members list */}
        <div className="flex items-center justify-between mt-4 mb-2">
          <p className="text-sm font-semibold" style={{ color: 'rgba(15,15,25,0.7)' }}>
            <span className="mr-1">{currentWorkspace?.members?.length}</span>
            <FormattedMessage id="MEMBERS" defaultMessage="Members" />
          </p>
        </div>
        <div className="max-h-[35vh] overflow-y-auto rounded-xl" style={{ border: '1px solid rgba(99,102,241,0.1)' }}>
          <WorkspaceMemberRow key={user?._id} member={{ ...user, role: workspaceRole }} workspaceId={workspaceId} isMember={isMember} />
          {currentWorkspace?.members?.map((member) => {
            if (member?._id === user?._id) return null;
            return <WorkspaceMemberRow key={member._id} member={member} workspaceId={workspaceId} isMember={isMember} />;
          })}
        </div>

        {/* Footer */}
        {!isMember && activeTab === 'email' && (
          <div className="flex justify-end mt-4">
            <button
              disabled={!canInvite}
              onClick={() => {
                if (!canInvite) return;
                dispatch(inviteToWorkspace({ workspaceId: workspaceId || '', invitees: selectedMembers }, () => { setSelectedMembers(null); closeModal(); }));
              }}
              className="flex items-center gap-1.5 h-8 px-4 rounded-xl text-sm font-semibold transition-all duration-150"
              style={canInvite
                ? { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', boxShadow: '0 2px 8px rgba(99,102,241,0.3)' }
                : { background: 'rgba(99,102,241,0.08)', color: 'rgba(99,102,241,0.35)', cursor: 'not-allowed' }}
            >
              <FormattedMessage id="INVITE" defaultMessage="Send Invite" />
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  currentWorkspace: state?.workspaceReducer?.currentWorkspace,
  user: state.userReducer.user,
  workspaceRole: state.userReducer.role
});

export default connect(mapStateToProps)(InviteToWorkspaceModal);
